<template>
  <div class="w-full">
    <div
      class="mx-auto flex w-full max-w-[1280px] flex-col gap-6 px-10 pb-20 pt-10 max-md:px-4 max-md:pb-10 max-md:pt-6"
    >
      <BlogBreadcrumbs :items="crumbs" variant="article" />

      <!-- Архивная статья: тело бэк не отдаёт, показываем заглушку -->
      <div v-if="article?.gone" class="rounded-2xl bg-white p-10 text-center max-md:p-6">
        <h1 class="text-2xl font-medium leading-[normal] text-txmain">{{ article.title }}</h1>
        <p class="mt-3 text-base text-txmain/80">Материал удалён и больше недоступен.</p>
        <NuxtLink
          to="/blog"
          class="mt-6 inline-block w-fit rounded-lg bg-[#2C6BFF] px-4 py-2.5 text-sm font-semibold text-white"
        >
          Все статьи
        </NuxtLink>
      </div>

      <template v-else-if="article">
        <header class="flex flex-col gap-6 max-md:gap-4">
          <div
            class="article-meta flex flex-wrap items-center gap-3 text-sm leading-[normal] text-txopacity"
          >
            <NuxtLink
              v-if="article.primaryTopic"
              :to="blogTopicUrl(article.primaryTopic.slug)"
              class="w-fit rounded-md bg-white px-3 py-1 text-xs font-semibold leading-[normal] text-[#2C6BFF] transition-colors hover:bg-[#EEF3FD]"
            >
              {{ article.primaryTopic.name }}
            </NuxtLink>
            <time v-if="article.publishedAt" :datetime="blogDateIso(article.publishedAt)">
              {{ blogDateLong(article.publishedAt) }}
            </time>
            <!-- Размер и цвет дублируем: не-scoped `div span` из app.vue задаёт
                 вложенным элементам 14px/#3a3d44 в обход наследования -->
            <span
              v-if="article.views != null"
              class="flex items-center gap-1 text-sm font-normal leading-[normal] text-txopacity"
            >
              <Icon name="lucide:eye" size="14" class="block text-txopacity" />
              {{ blogViews(article.views) }}
            </span>
          </div>

          <!-- text-[24px], а не text-2xl: последний навязал бы свой
               line-height 32 вместо 1.2 из макета -->
          <h1
            class="break-words text-[40px] font-medium leading-[1.2] text-txmain max-md:text-[24px]"
          >
            {{ article.title }}
          </h1>

          <div v-if="article.author" class="flex items-center gap-2 pb-3">
            <NuxtImg
              v-if="article.author.avatar"
              :src="article.author.avatar"
              :alt="article.author.name"
              class="size-6 shrink-0 rounded-full object-cover"
            />
            <span v-else class="size-6 shrink-0 rounded-full bg-[#DDD]" />
            <p class="text-sm leading-[normal] text-txmain">
              {{ article.author.name
              }}<template v-if="article.author.position">, {{ article.author.position }}</template>
            </p>
          </div>
        </header>

        <div class="flex items-start gap-10 max-lg:flex-col">
          <article class="flex min-w-0 flex-1 flex-col gap-8">
            <NuxtImg
              v-if="article.cover"
              :src="article.cover"
              :alt="article.title"
              class="aspect-[820/480] w-full rounded-2xl bg-[#E5EBF5] object-cover"
            />

            <!-- Оглавление: якоря бэк уже проставил в html блоков, свои не генерируем -->
            <nav
              v-if="article.toc?.length"
              class="rounded-2xl border border-[#E5EBF5] bg-[#EEF3FD] p-6"
            >
              <p class="mb-3 text-base font-bold text-txmain">Содержание</p>
              <ul class="flex flex-col gap-2">
                <li
                  v-for="item in article.toc"
                  :key="item.anchor"
                  :style="{ paddingLeft: `${Math.max(0, item.level - 2) * 14}px` }"
                >
                  <a :href="`#${item.anchor}`" class="text-sm text-[#2C6BFF] hover:text-[#003DCD]">
                    {{ item.text }}
                  </a>
                </li>
              </ul>
            </nav>

            <BlogArticleBlocks :blocks="article.content || []" />

            <div v-if="article.tags?.length" class="flex flex-wrap gap-2">
              <NuxtLink
                v-for="tag in article.tags"
                :key="tag.id"
                :to="blogTagUrl(tag.slug)"
                class="w-fit rounded-md bg-[#EEF3FD] px-2 py-1.5 text-sm font-medium leading-[normal] text-[#2C6BFF] transition-colors hover:bg-[#DEE9FF]"
              >
                {{ tag.name }}
              </NuxtLink>
            </div>

            <section class="flex flex-col gap-4">
              <h2 class="text-2xl font-medium leading-[normal] text-txmain">Поделиться статьёй</h2>
              <div class="flex flex-wrap items-center gap-3">
                <a
                  v-for="item in shareTargets"
                  :key="item.label"
                  :href="item.url"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  class="share-btn"
                >
                  <UINewIconsSocialIMax v-if="!item.icon" class="size-6 shrink-0" />
                  <Icon v-else :name="item.icon" size="24" />
                  {{ item.label }}
                </a>
                <button type="button" class="share-btn" @click="copyLink">
                  <span
                    class="flex size-5 items-center justify-center rounded-[10px] bg-[#2C6BFF]"
                  >
                    <Icon name="lucide:link" size="11" class="share-copy-icon block" />
                  </span>
                  {{ copyLabel }}
                </button>
              </div>
            </section>

            <!-- Блок «Об авторе» -->
            <section v-if="article.author" class="flex flex-col gap-4">
              <h2 class="text-2xl font-medium leading-[normal] text-txmain">Об авторе</h2>
              <div class="flex gap-4 rounded-2xl border border-[#E5EBF5] bg-white p-5">
                <NuxtImg
                  v-if="article.author.avatar"
                  :src="article.author.avatar"
                  :alt="article.author.name"
                  class="size-14 shrink-0 rounded-full object-cover"
                />
                <span
                  v-else
                  class="flex size-14 shrink-0 items-center justify-center rounded-full bg-[#DDD] text-white"
                >
                  <Icon name="lucide:user" size="24" class="block text-white" />
                </span>

                <div class="flex min-w-0 flex-1 flex-col gap-2">
                  <p class="text-lg font-bold leading-[normal] text-txmain">
                    {{ article.author.name }}
                  </p>
                  <p v-if="article.author.position" class="text-sm leading-[normal] text-txmain/80">
                    {{ article.author.position }}
                  </p>
                  <p v-if="article.author.bio" class="text-sm leading-[1.6] text-txmain">
                    {{ article.author.bio }}
                  </p>
                  <NuxtLink
                    v-if="article.author.slug"
                    :to="{ path: '/blog', query: { author: article.author.slug } }"
                    class="w-fit text-sm font-medium leading-[normal] text-[#2C6BFF] hover:text-[#003DCD]"
                  >
                    Все статьи автора
                  </NuxtLink>
                  <div v-if="authorLinks.length" class="flex flex-wrap gap-3">
                    <a
                      v-for="link in authorLinks"
                      :key="link"
                      :href="link"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      class="text-sm text-[#2C6BFF] hover:text-[#003DCD]"
                    >
                      {{ hostOf(link) }}
                    </a>
                  </div>
                </div>
              </div>
            </section>
          </article>

          <BlogSidebar
            class="w-[340px] shrink-0 max-lg:hidden lg:sticky lg:top-24"
            :tags="tags"
            :popular="popular"
            @search="goToSearch"
          />
        </div>

        <!-- Похожие статьи: подбираем по тегам открытой статьи -->
        <section v-if="related.length" class="flex flex-col gap-6 pt-10 max-md:pt-4">
          <h2 class="text-2xl font-medium leading-[normal] text-txmain">Похожие статьи</h2>
          <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <BlogCard
              v-for="item in related"
              :key="item.id"
              :article="item"
              variant="related"
            />
          </div>
        </section>
      </template>
    </div>
  </div>

  <PublicFooter />
</template>

<script setup lang="ts">
  import BlogService from "~/services/BlogService";
  import { blogArticleUrl, blogTagUrl, blogTopicUrl } from "~/common/blogUrl";
  import { blogDateIso, blogDateLong, blogViews } from "~/common/blogFormat";

  definePageMeta({ layout: "base" });

  const route = useRoute();
  const requestUrl = useRequestURL();

  const slug = computed(() => String(route.params.slug || ""));

  const { data: article, error } = await useAsyncData(
    "blog-article",
    () => BlogService.getPublicArticle(slug.value),
    { watch: [slug] }
  );

  // Несуществующая статья и черновик — 404 (бэк отдаёт код 3300)
  if (error.value || !article.value) {
    throw createError({ statusCode: 404, statusMessage: "Статья не найдена" });
  }

  // Слаг меняли — старый адрес ведёт на актуальный, вес ссылок не теряется
  if (article.value.redirectTo) {
    await navigateTo(
      blogArticleUrl({
        slug: article.value.redirectTo,
        primaryTopic: article.value.primaryTopic,
      }),
      { redirectCode: 301 }
    );
  }

  // Статья снята с публикации
  if (article.value.gone && import.meta.server) {
    setResponseStatus(useRequestEvent()!, 410);
  }

  // Канонизация темы: одна статья не должна открываться по нескольким адресам
  const topicSlug = computed(() => article.value?.primaryTopic?.slug);
  if (!article.value.gone && topicSlug.value && route.params.topic !== topicSlug.value) {
    await navigateTo(blogArticleUrl(article.value), { redirectCode: 301 });
  }

  // ===== Боковая панель и подборки =====
  // Все три запроса вспомогательные: их падение не должно ронять страницу
  // статьи, поэтому ошибку гасим и просто не рисуем блок.
  const tagSlugs = computed<string[]>(() =>
    (article.value?.tags ?? []).map((tag: any) => tag.slug).filter(Boolean)
  );

  const { data: relatedData } = await useAsyncData(
    "blog-article-related",
    () =>
      tagSlugs.value.length
        ? BlogService.getPublicArticles({
            tagSlugs: tagSlugs.value,
            sortFields: ["id"],
            sortDirections: ["desc"],
            perPage: 5,
          }).catch(() => null)
        : Promise.resolve(null),
    { watch: [tagSlugs] }
  );

  const { data: popularData } = await useAsyncData("blog-popular", () =>
    BlogService.getPopularArticles(5).catch(() => null)
  );

  const { data: tagsData } = await useAsyncData("blog-tags", () =>
    BlogService.getPublicTags().catch(() => null)
  );

  const popular = computed(() => popularData.value?.records ?? []);
  const tags = computed(() => tagsData.value?.records ?? []);

  const related = computed(() => {
    const byTags = (relatedData.value?.records ?? []).filter(
      (item: any) => item.id !== article.value?.id
    );
    // Без тегов опираемся на подборку бэка — она собрана по теме статьи
    return (byTags.length ? byTags : (article.value?.related ?? [])).slice(0, 4);
  });

  const goToSearch = (payload: { search: string; searchIn: string }) => {
    navigateTo({ path: "/blog", query: { search: payload.search, searchIn: payload.searchIn } });
  };

  const crumbs = computed(() => {
    const items = [
      { name: "Главная", url: "/" },
      { name: "Блог", url: "/blog" },
    ];
    if (article.value?.primaryTopic) {
      items.push({
        name: article.value.primaryTopic.name,
        url: blogTopicUrl(article.value.primaryTopic.slug),
      });
    }
    if (article.value?.title) {
      items.push({ name: article.value.title, url: blogArticleUrl(article.value) });
    }
    return items;
  });

  const authorLinks = computed<string[]>(() => {
    const links = article.value?.author?.links;
    if (Array.isArray(links)) return links.filter(Boolean);
    if (links && typeof links === "object") return Object.values(links).filter(Boolean) as string[];
    return [];
  });

  const hostOf = (url: string) => {
    try {
      return new URL(url).hostname.replace(/^www\./, "");
    } catch {
      return url;
    }
  };

  // ===== SEO =====
  // Всё готово в article.seo — своей логики подстановки не нужно.
  const seo = computed(() => article.value?.seo ?? {});
  const canonical = computed(
    () => seo.value.canonicalUrl || `${requestUrl.origin}${blogArticleUrl(article.value ?? {})}`
  );

  useHead(() => ({
    title: seo.value.title || article.value?.title,
    link: [{ rel: "canonical", href: canonical.value }],
    meta: [
      ...(seo.value.description ? [{ name: "description", content: seo.value.description }] : []),
      ...(seo.value.keywords ? [{ name: "keywords", content: seo.value.keywords }] : []),
      ...(seo.value.isIndexable === false
        ? [{ name: "robots", content: "noindex, nofollow" }]
        : []),
      { property: "og:type", content: "article" },
      { property: "og:title", content: seo.value.title || article.value?.title },
      ...(seo.value.description
        ? [{ property: "og:description", content: seo.value.description }]
        : []),
      { property: "og:url", content: canonical.value },
      ...(article.value?.cover ? [{ property: "og:image", content: article.value.cover }] : []),
      ...(article.value?.publishedAt
        ? [{ property: "article:published_time", content: article.value.publishedAt }]
        : []),
    ],
    // Микроразметка: BlogPosting + BreadcrumbList (ТЗ, раздел 11)
    script: [
      {
        type: "application/ld+json",
        innerHTML: JSON.stringify([
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: article.value?.title,
            description: article.value?.excerpt || seo.value.description || undefined,
            image: article.value?.cover || undefined,
            datePublished: article.value?.publishedAt || undefined,
            dateModified: article.value?.updatedAt || article.value?.publishedAt || undefined,
            mainEntityOfPage: canonical.value,
            publisher: {
              "@type": "Organization",
              name: "Telepost",
              logo: {
                "@type": "ImageObject",
                url: `${requestUrl.origin}/assets/img/logo.svg`,
              },
            },
            ...(article.value?.author
              ? {
                  author: {
                    "@type": "Person",
                    name: article.value.author.name,
                    jobTitle: article.value.author.position || undefined,
                  },
                }
              : {}),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            // У бэка крошки уже с position — если их нет, считаем сами
            itemListElement: (article.value?.breadcrumbs?.length
              ? article.value.breadcrumbs
              : crumbs.value.map((c, i) => ({ name: c.name, url: c.url, position: i + 1 }))
            ).map((b: any) => ({
              "@type": "ListItem",
              position: b.position,
              name: b.name,
              item: b.url?.startsWith("http") ? b.url : `${requestUrl.origin}${b.url}`,
            })),
          },
        ]),
      },
    ],
  }));

  // ===== Просмотр и «поделиться» =====
  const shareTargets = computed(() => {
    const url = encodeURIComponent(canonical.value);
    const text = encodeURIComponent(article.value?.title || "");
    return [
      {
        label: "Telegram",
        icon: "logos:telegram",
        url: `https://t.me/share/url?url=${url}&text=${text}`,
      },
      { label: "VK", icon: "mingcute:vkontakte-fill", url: `https://vk.com/share.php?url=${url}` },
      // У Max нет отдельного параметра под ссылку: адрес кладётся в text.
      // Иконки Max в iconify нет — рисуем компонентом из UINew.
      { label: "Max", icon: "", url: `https://max.ru/:share?text=${url}` },
    ];
  });

  const copyLabel = ref("Copy link");

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(canonical.value);
      copyLabel.value = "Ссылка скопирована";
    } catch {
      copyLabel.value = "Не удалось скопировать";
    }
    setTimeout(() => (copyLabel.value = "Copy link"), 2000);
  };

  onMounted(() => {
    // Считаем просмотр только с клиента: на SSR попадали бы краулеры
    if (article.value?.id && !article.value?.gone) {
      BlogService.countView(article.value.id).catch(() => {});
    }
  });
</script>

<style lang="scss" scoped>
  // Нейтрализуем не-scoped `a { width: 100% }` из меню лейаута: без этого
  // теги, кнопки «поделиться» и ссылки автора растягиваются на всю ширину
  a {
    width: auto;
  }

  .share-btn {
    @apply flex h-10 w-fit items-center gap-2.5 rounded-lg border border-[#E5EBF5] bg-[#EEF3FD] px-3 text-xs font-medium leading-[normal] text-txmain transition-colors hover:border-[#2C6BFF]/30 hover:bg-[#DEE9FF];
  }

  // nuxt-icon рисует иконку маской и красит её background-color: currentColor,
  // поэтому цвет задаём обоими способами — для css- и для svg-режима
  .share-copy-icon {
    color: #fff;
    background-color: #fff;
  }

  // Точку между элементами меты рисуем стилями: при пустых полях в разметке
  // оставалась висячая точка
  .article-meta > * + *::before {
    content: "·";
    margin-right: 12px;
  }
</style>
